<template>
  <div
    class="multiple-choice-type"
  >
    <div class="form-group">
      <label class="label-option">{{ $i18nPresenter.t('Scale') }}</label>
      <div class="form-row flex-nowrap">
        <div class="form-group col-md-9 col-8">
          <label for="low-label">{{ $i18nPresenter.t('Low label') }}</label>
          <InputText
            :place-holder="$i18nPresenter.t('Enter label…')"
            :id-name="'low-label'"
            :index="-1"
            :limit="30"
            :val="$store.state.slideActive.scale.lowLabel"
            @interface="(d) => updateScaleLabel('scale.lowLabel')(d)"
          />
        </div>
        <div class="form-group col-md-3">
          <label for="low-value">{{ $i18nPresenter.t('Low value') }}</label>
          <input
            class="form-control input-number"
            :class="{'error': errorMessage}"
            type="number"
            id="low-value"
            :value="$store.state.slideActive.scale.lowValue"
            @input="checkHasAnswerDebounce('lowValue', $event)"
            :disabled="$store.state.slideActive.scaleQuestionLoaded === false"
          >
        </div>
      </div>

      <label
        v-if="allowMidValues"
        @click="toggleMidValues"
        class="pointer"
      >
        {{ $i18nPresenter.t('Set mid-values') }}
        <i
          :class="{
            'fas fa-angle-up': $store.state.slideActive.scale.showMidValues,
            'fas fa-angle-down': !$store.state.slideActive.scale.showMidValues
          }"
        />
      </label>
      <div
        v-if="allowMidValues && $store.state.slideActive.scale.showMidValues"
      >
        <div
          class="form-row flex-nowrap"
          v-for="(item, index) in midValues"
          :key="item.index"
        >
          <div class="form-group col-md-9 col-8">
            <InputText
              :place-holder="$i18nPresenter.t('Enter label…')"
              :id-name="'mid-label-'"
              :index="index"
              :limit="30"
              :val="$store.state.slideActive.scale[index]"
              @interface="(d) => updateScaleLabel('scale.' + index)(d)"
            />
          </div>
          <div class="form-group col-md-3">
            <input
              class="form-control input-number disabled"
              type="number"
              disabled
              :id="'mid-value-' + index"
              :value="item"
              :index="index"
            >
          </div>
        </div>
      </div>

      <div class="form-row flex-nowrap">
        <div class="mb-1 col-md-9 col-8">
          <label for="low-label">{{ $i18nPresenter.t('High label') }}</label>
          <InputText
            :place-holder="$i18nPresenter.t('Enter label…')"
            :id-name="'high-label'"
            :index="-1"
            :limit="30"
            :val="$store.state.slideActive.scale.highLabel"
            @interface="(d) => updateScaleLabel('scale.highLabel')(d)"
          />
        </div>
        <div class="mb-1 col-md-3">
          <label for="high-value">{{ $i18nPresenter.t('High value') }}</label>
          <input
            class="form-control input-number"
            :class="{'error': errorMessage}"
            type="number"
            id="high-value"
            :value="$store.state.slideActive.scale.highValue"
            @input="checkHasAnswerDebounce('highValue', $event)"
            :disabled="$store.state.slideActive.scaleQuestionLoaded === false"
          >
        </div>

        <!-- confirm clean data -->
        <ConfirmModal
          :data="dataConfirmClean"
          @handleModalConfirmOk="handleModalConfirmCleanOk"
          @handleModalConfirmCancel="handleModalConfirmCleanCancel"
          v-if="isConfirmClean"
        />
      </div>

      <AhaInfoBox v-if="!allowMidValues">
        {{ $i18nPresenter.t('When the difference between the High value and Low value is between 2 to 10, you can set custom labels for the mid-values.') }}
      </AhaInfoBox>

      <div class="small-90 text-danger" v-if="errorMessage">
        {{ errorMessage }}
      </div>
    </div>
  </div>
</template>

<script>
import debounce from 'debounce'
import { mapActions, mapState } from 'vuex'
import SlideApi from '@/api/slide'
import InputText from '@/components/presentations/InputText'
import ConfirmModal from '@/components/ConfirmModal.vue'
import EventBus from '@/eventBus'
import Util from '@/util'

export default {
  name: 'ScaleQuestion',
  components: {
    InputText,
    ConfirmModal,
  },
  data() {
    return {
      errorMessage: '',
      isConfirmClean: false,
      dataConfirmClean: {
        title: 'Confirm changing scale values',
        description: 'Some audience already responded. If you change the scale values now, all current responses must be erased. Are you sure?',
        textOk: 'Confirm',
        styleOk: 'btn-light',
        styleLoading: 'black',
        styleCancel: 'btn-primary',
        isLoading: false,
      },
      checkHasAnswerKey: null,
      checkHasAnswerValue: null,
      checkHasAnswerOld: null,
      defaultScale: {
        highLabel: this.$i18nPresenter.t('Strongly agree'),
        lowLabel: this.$i18nPresenter.t('Strongly disagree'),
        highValue: 5,
        lowValue: 1,
        0: '',
        1: '',
        2: '',
        3: '',
        4: '',
        5: '',
        6: '',
        7: '',
        8: '',
        9: '',
        showAverage: false,
        mustRate: true,
        showMidValues: false,
      },
    }
  },
  computed: {
    ...mapState({
      slideActive: (state) => state.slideActive,
      presentation: (state) => state.presentation,
    }),
    allowMidValues() {
      return this.slideActive.scale.highValue -
       this.slideActive.scale.lowValue <= 10
       && this.slideActive.scale.highValue -
       this.slideActive.scale.lowValue > 1
    },
    midValues() {
      return Array.from(
        {
          length: parseInt(this.slideActive.scale.highValue, 10) -
          parseInt(this.slideActive.scale.lowValue, 10) - 1,
        },
        (_, i) => i + parseInt(this.slideActive.scale.lowValue, 10) + 1,
      )
    },
  },

  watch: {
    '$store.state.presentation.language': function watchChangeLanguage() {
      this.defaultScale.highLabel = this.$i18nPresenter.t('Strongly agree')
      this.defaultScale.lowLabel = this.$i18nPresenter.t('Strongly disagree')
    },
  },

  created() {
    if (!this.slideActive.scale) {
      this.updateSlideActiveAction({
        scale: this.defaultScale,
      })
    }
  },

  mounted() {
    this.validateData()
  },

  methods: {
    ...mapActions(['updateSlideActiveAction']),
    toggleMidValues() {
      this.updateSlideActiveAction({
        scale: {
          ...this.slideActive.scale,
          showMidValues: !this.slideActive.scale.showMidValues,
        },
      })
      this.updateSlideDebounce()
    },

    validateData() {
      this.errorMessage = ''

      if (!this.slideActive.scale) {
        return
      }

      let lowValue = parseInt(this.slideActive.scale.lowValue || 0, 10)
      lowValue = Number.isNaN(lowValue) ? 1 : lowValue

      let highValue = parseInt(this.slideActive.scale.highValue || 0, 10)
      highValue = Number.isNaN(highValue) ? 5 : highValue

      if (highValue > lowValue + 1000) {
        this.errorMessage = this.$i18nPresenter.t('The max difference between the High and Low values is 1000.')
      }

      if (highValue <= lowValue) {
        this.errorMessage = this.$i18nPresenter.t('highValueMustBeGreaterThanLowValue', { highValue, lowValue })
      }

      if (!this.errorMessage) {
        this.updateSlideActiveAction({
          scale: {
            ...this.slideActive.scale,
            lowValue,
            highValue,
          },
        })
      }
    },

    checkHasAnswerDebounce: debounce(function (key, e) {
      this.checkHasAnswerKey = key
      this.checkHasAnswerValue = e.target.value
      this.checkHasAnswerOld = this.slideActive.scale[this.checkHasAnswerKey]

      if (this.slideActive.hasScaleQuestionAnswer) {
        this.isConfirmClean = true
      } else {
        this.updateNewValue()
      }
    }, 1000),

    handleModalConfirmCleanOk() {
      this.dataConfirmClean.isLoading = true
      this.updateSlideActiveAction({
        scale: {
          ...this.slideActive.scale,
          [this.checkHasAnswerKey]: this.checkHasAnswerValue,
        },
      })
      this.dataConfirmClean.isLoading = false
      this.clearSlideData({ item: { ...this.slideActive } })
      this.updateSlideSync(null, true)
      Util.mixpanelTracking(
        'Slide > Content Tab > Confirm change Scale Values',
        'Confirm button press',
        'Presenter confirmed',
      )
    },

    handleModalConfirmCleanCancel() {
      this.updateSlideActiveAction({
        scale: {
          ...this.slideActive.scale,
          [this.checkHasAnswerKey]: this.checkHasAnswerOld,
        },
      })
      this.isConfirmClean = false
      Util.mixpanelTracking(
        'Slide > Content Tab > Confirm change Scale Values',
        'Cancel button press',
        'Presenter canceled',
      )
    },

    updateNewValue(resetOldValue) {
      this.updateSlideActiveAction({
        scale: {
          ...this.slideActive.scale,
          [this.checkHasAnswerKey]: this.checkHasAnswerValue,
        },
      })
      this.updateSlideSync(() => {
        EventBus.$emit('reloadScaleChart')
      }, resetOldValue)
    },

    updateScaleLabel(key) {
      return (data) => {
        if (key.indexOf('scale.') === 0) {
          const temp = typeof data === 'object' ? data.value : data

          this.updateSlideActiveAction({
            scale: {
              ...this.slideActive.scale,
              [key.slice('scale.'.length)]: temp,
            },
          })

          this.updateSlideDebounce()
        }
      }
    },

    updateSlideDebounce: debounce(function () { this.updateSlideSync() }, 500),

    updateSlideSync(cb, resetOldValue) {
      this.validateData()
      if (this.errorMessage) {
        if (resetOldValue) {
          this.updateSlideActiveAction({
            scale: {
              ...this.slideActive.scale,
              [this.checkHasAnswerKey]: this.checkHasAnswerOld,
            },
          })
          this.validateData()
        }
        return
      }

      const updateState = () => {
        const { id } = this.slideActive
        const index = this.$store.state.presentation.slides.findIndex((item) => id === item.id)
        if (!this.presentation.slides[index]) return
        this.presentation.slides[index].scale = this.slideActive.scale
      }

      this.$emit('updateSlide', 14, () => {
        updateState()
        if (cb) cb()
      })
    },

    _resetSlideDataInStore(index) {
      this.presentation.slides[index].SlideOptions.forEach((slideOption) => {
        slideOption.count = 0
      })
      this.presentation.slides[index].multipleCountAnswer = 0
      this.presentation.slides[index].scale = this.slideActive.scale
      this.presentation.slides[index].hasScaleQuestionAnswer = false
    },

    async clearSlideData(data) {
      this.isConfirmClean = false
      const index = this.$store.state.presentation.slides
        .findIndex((item) => data.item.id === item.id)
      if (this.presentation.slides[index]) {
        this._resetSlideDataInStore(index)
      }

      const payload = {
        presentationId: this.presentation.id,
        slideId: [data.item.id],
      }

      Object.keys(this.$store.state.audiences).forEach((key) => {
        delete this.$store.state.audiences[key][data.item.id]
      })

      try {
        await SlideApi.clearSlides(payload)

        this.updateSlideActiveAction({
          hasScaleQuestionAnswer: false,
          multipleCountAnswer: 0,
        })
        this.$store.state.tempAudienceCount = 0
        EventBus.$emit('reloadScaleChart')
      } catch {
        // do nothing
      }
    },
  },
}
</script>
